import React, { memo, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import ApexCharts from 'apexcharts';

import { Width } from '../../Context/WidthContext';

export default memo(LegacyGaugeWidget);

function LegacyGaugeWidget(props) {
  const vw = useContext(Width);
  const {
    datapointID,
    key,
    max,
    min,
    secondGauge: {
      datapointID: datapointID1,
      key: key1,
      max: max1,
      min: min1,
    } = {},
    title,
  } = props.widget?.settings || {};

  const lines = [
    { cID: datapointID, k: key, mn: min, mx: max },
    { cID: datapointID1, k: key1, mn: min1, mx: max1 },
  ].reduce((acc, { cID, k, mn, mx }, i) => {
    if (!cID) return acc;

    const { signals = {} } = props.fetchedData?.get?.(cID)?.[0] || {};
    const { value } =
      Object.values(signals || {})?.find(({ name }) => name === k) || {};
    switch (true) {
      case Number.isFinite(mx) && value >= mx:
      case Number.isFinite(mn) && value <= mn:
        acc[i] = {
          data: [value >= mx ? 100 : 0],
          datapointID: cID,
          max: mx,
          min: mn,
          name: k,
          originalValue: value,
        };
        break;
      default: {
        const calculated = ((value - mn) * 100) / (mx - mn);
        const data = Number.isFinite(calculated) ? [calculated] : [];
        acc[i] = {
          data,
          datapointID: cID,
          max: mx,
          min: mn,
          name: k,
          originalValue: value,
        };
        break;
      }
    }
    return acc;
  }, []);

  const height = useMemo(() => {
    switch (true) {
      case lines.length === 2:
        return '95%';
      default:
        return '120%';
    }
  }, [lines.length, vw]);

  return lines.map((line, index) => {
    const options = new GaugeGraphOptionsBuilder(
      title,
      props.graphWidgetIDs,
      props.graphIndex,
      props.renderNextGraph,
      index
    ).addOptions(line, props.fetchedData, props.signalUnitsMap);
    return (
      <div
        className={`d-inline-block h-100 w-${
          lines?.length === 2 ? '50' : '100'
        } position-relative`}
        key={`${title}-${index}`}
      >
        <GaugeWrapper
          data={line?.data?.[0]}
          height={height}
          name={line?.name}
          options={options}
          showName={lines?.length === 2}
        />
      </div>
    );
  });
}

LegacyGaugeWidget.propTypes = {
  fetchedData: PropTypes.object,
  graphIndex: PropTypes.number,
  graphWidgetIDs: PropTypes.arrayOf(PropTypes.string),
  renderNextGraph: PropTypes.func,
  signalUnitsMap: PropTypes.object,
  widget: PropTypes.shape({
    _id: PropTypes.string,
    settings: PropTypes.shape({
      datapoint: PropTypes.object,
      datapointID: PropTypes.string,
      key: PropTypes.string,
      max: PropTypes.number,
      secondGauge: PropTypes.shape({
        datapoint: PropTypes.object,
        datapointID: PropTypes.string,
        key: PropTypes.string,
        max: PropTypes.number,
      }),
      title: PropTypes.string,
    }),
  }),
};

class GaugeGraphOptionsBuilder {
  constructor(id, graphWidgetIDs, graphIndex, renderNextGraph, index) {
    this.chart = {
      events: {
        mounted: () => {
          if (graphWidgetIDs?.length - 1 > graphIndex) {
            renderNextGraph(graphWidgetIDs[graphIndex + 1]);
          }
        },
      },
      fontFamily: '"Roboto", sans-serif',
      id: `${id}-${graphIndex}-${index}`,
    };
    this.noData = { text: 'No data available for this signal.' };
    this.stroke = { width: 1.5 };
    this.xaxis = {};
  }

  addOptions(line, fetchedData, signalUnitsMap = new Map()) {
    const { name, datapointID, max, min, originalValue } = line || {},
      { display_precision, unit } =
        signalUnitsMap.get(datapointID)?.[name] ?? {},
      inRange = originalValue < max && originalValue > min;

    this.labels = [unit || ''];
    this.plotOptions = {
      radialBar: {
        startAngle: -135,
        endAngle: 135,
        dataLabels: {
          name: {
            color: inRange ? '#878787' : '#4196D6',
            fontFamily: '"Roboto", sans-serif',
            fontSize: '16px',
            offsetY: 25,
          },
          value: {
            offsetY: -12,
            fontSize: '28px',
            color: inRange ? '#4196D6' : '#878787',
            formatter: (val) => {
              const displayVal = Number.isInteger(originalValue)
                ? originalValue
                : originalValue.toFixed(display_precision ?? 2);
              return val <= 100 && val >= 0
                ? displayVal
                : val > 100
                  ? `> ${max}`
                  : `< ${min}`;
            },
          },
        },
      },
    };
    this.fill = {
      gradient: {
        enabled: true,
        shade: 'dark',
        shadeIntensity: 0.15,
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
      },
    };
    this.stroke = { dashArray: 4 };
    this.states = { hover: { filter: { type: 'none' } } };

    return this;
  }
}

function GaugeWrapper(props) {
  const series = useMemo(
    () => (!props.data && props.data !== 0 ? [] : [props.data]),
    [props.data]
  );
  useEffect(() => {
    if (!props.data && props.data !== 0) return;
    ApexCharts.exec(props.options.chart.id, 'updateSeries', series);
    ApexCharts.exec(props.options.chart.id, 'updateOptions', props.options);
  }, [props.data, props.options, series]);

  return (
    <div className="position-absolute h-100 w-100 d-flex flex-column align-items-center justify-content-center">
      <div className="flex-fill w-100 position-relative">
        <div className="position-absolute h-100 w-100">
          <Chart
            height="100%"
            options={props.options}
            series={series}
            type="radialBar"
          />
        </div>
      </div>
      {props.showName ? (
        <p className="mb-0 px-1 px-md-3 text-center text-bold three-quarter-text">
          {props.name}
        </p>
      ) : null}
    </div>
  );
}

GaugeWrapper.propTypes = {
  data: PropTypes.number,
  height: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.object,
  showName: PropTypes.bool,
};
